<script setup lang="ts">
import { getUniqueId, useUserStore } from '@/store/userStore';
import { useRouter } from 'vue-router';
import { paths } from '@/utils/paths';
import useResponsive from '@/utils/responsive-util';
import { authService } from '@/services/auth.service';
import { Locales } from '@/store/language.interface';
import { getLabel } from '@/utils/data-display-utils';
import { ref } from 'vue';
import { useMenuKeyboardNavigation } from '@/composables/useMenuKeyboardNavigation';

// PROPS
const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// STORE
const userStore = useUserStore();

// CONST
const router = useRouter();
const { isMobile } = useResponsive();
const profileItemRef = ref<HTMLButtonElement | null>(null);
const logoutItemRef = ref<HTMLButtonElement | null>(null);
const { isMenuOpen, triggerRef, closeOnChoice, onMenuShow, onMenuHide, onMenuKeydown } = useMenuKeyboardNavigation([
  profileItemRef,
  logoutItemRef,
]);

// FUNCTIONS
function logout() {
  localStorage.removeItem('accessToken');
  router.push(paths.root);
  authService.logout();
}

function getUserName() {
  if (userStore.user && userStore.user.id) {
    return userStore.getUserName ? userStore.getUserName : getUniqueId(userStore.user);
  }
}

function goToProfilePage() {
  closeOnChoice();
  router.push(paths.profile);
}

function onLogout() {
  closeOnChoice();
  logout();
}
</script>

<template>
  <div class="menu-container">
    <q-btn
      v-if="!isMobile"
      ref="triggerRef"
      flat
      unelevated
      class="menu-container__trigger"
      :aria-label="getLabel('aria-label.header.account-menu', props.languageEnabled)"
      aria-haspopup="menu"
      :aria-expanded="isMenuOpen"
    >
      <span class="menu-container__user">{{ getUserName() }}</span>
      <q-icon class="menu-container__expand-icon" name="expand_more" aria-hidden="true" />
      <q-menu
        fit
        class="menu-container__account-menu"
        :offset="[0, 8]"
        v-model="isMenuOpen"
        no-refocus
        @show="onMenuShow"
        @hide="onMenuHide"
      >
        <q-list role="menu" aria-orientation="vertical" @keydown="onMenuKeydown">
          <q-item tag="div" role="none" class="menu-container__account-menu--item">
            <q-item-section>
              <button
                ref="profileItemRef"
                type="button"
                class="menu-container__account-menu-button"
                role="menuitem"
                @click="goToProfilePage()"
              >
                {{ getLabel('account.my-profile', props.languageEnabled) }}
              </button>
            </q-item-section>
          </q-item>
          <q-item tag="div" role="none" class="menu-container__account-menu--item">
            <q-item-section>
              <button
                ref="logoutItemRef"
                type="button"
                class="menu-container__account-menu-button"
                role="menuitem"
                @click="onLogout()"
              >
                {{ getLabel('account.logout', props.languageEnabled) }}
              </button>
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-btn>
  </div>
</template>

<style lang="scss">
.menu-container {
  display: flex;
  align-items: center;

  &__trigger {
    color: $color-neutral-black;
    transition: 200ms;
    padding: 4px 8px;

    &:hover {
      color: $color-primary;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  &__account-menu {
    padding: 16px;

    &--item {
      padding: 0;
    }

    &-button {
      width: 100%;
      padding: $space-sm 0;
      border: none;
      background: transparent;
      color: inherit;
      text-align: left;
      cursor: pointer;
      transition: 200ms;
      font: inherit;
      -webkit-user-select: none; /* Safari */
      -moz-user-select: none; /* Firefox */
      -ms-user-select: none; /* IE10+/Edge */
      user-select: none; /* Standard */

      &:hover {
        color: $color-primary;
      }

      &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
        border-radius: 2px;
      }
    }
  }

  &__user {
    margin: auto;
    font-weight: bold;
    padding-left: $space-sm;
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+/Edge */
    user-select: none; /* Standard */
  }

  &__expand-icon {
    font-size: $medium-title !important;
    transition: 200ms;

    &:hover {
      color: $color-primary;
    }
  }

  .q-icon {
    font-size: $small-text;
    cursor: pointer;
  }
}

// .q-badge {
// 	position: absolute;
// 	top: 4px;
// 	right: 210px;
// 	width: 12px;
// 	border-radius: 50% !important;
// 	background-color: $color-primary !important;
// }

.profile-done {
  &__icon {
    padding: 0 !important;
    font-size: $xxsmall-text !important;
    right: 4px;
    font-weight: bold !important;
    color: white !important;
  }
}

@media screen and (max-width: $breakpoint-md) {
  .user {
    display: none;
  }
}
</style>
